/**
 * IdVerzoekModal — pop-up bij inloggen wanneer er een openstaand ID-verzoek is.
 * Wegklikbaar ("Later"), maar verschijnt bij elke nieuwe login opnieuw totdat geüpload.
 * Medewerker kan scannen/foto's maken (camera op mobiel) of bestanden uploaden (1..N).
 */
import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Camera, Upload, IdCard, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function IdVerzoekModal() {
  const { user } = useAuth();
  const [verzoek, setVerzoek] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bestanden, setBestanden] = useState([]);
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        // Samenloop-preventie: verplichte NoodcontactModal heeft voorrang.
        // Zolang die actief is, tonen we het ID-verzoek niet (komt volgende login terug).
        try {
          const { data: nc } = await api.get("/me/noodcontact-status");
          if (nc?.is_verplicht_actie) return;
        } catch { /* status niet bereikbaar → gewoon doorgaan */ }
        const { data } = await api.get("/me/id-verzoek");
        if (cancelled || !data?.open) return;
        const dismissKey = `id_verzoek_dismissed_${data.verzoek.id}`;
        if (sessionStorage.getItem(dismissKey)) return;
        setVerzoek(data.verzoek);
        setOpen(true);
      } catch { /* stil falen, login niet blokkeren */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const voegToe = (files) => {
    if (!files?.length) return;
    setBestanden((prev) => [...prev, ...Array.from(files)]);
  };

  const later = () => {
    if (verzoek) sessionStorage.setItem(`id_verzoek_dismissed_${verzoek.id}`, "1");
    setOpen(false);
  };

  const upload = async () => {
    if (!bestanden.length) { toast.error("Maak eerst een foto of kies een bestand."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      bestanden.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/me/id-verzoek/${verzoek.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
      });
      toast.success(`Bedankt! ${data.aantal} bestand(en) veilig geüpload.`);
      (data.geweigerd || []).forEach((g) => toast.error(`${g.bestand}: ${g.reden}`));
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload mislukt, probeer het opnieuw.");
    } finally {
      setBusy(false);
    }
  };

  if (!verzoek) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) later(); }}>
      <DialogContent className="max-w-lg" data-testid="id-verzoek-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IdCard className="h-5 w-5 text-[hsl(var(--brand-red))]" />
            {verzoek.type_label} uploaden
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Alert>
            <AlertDescription className="text-sm">
              <b>Beste {user?.name},</b>
              <div className="mt-1">
                {verzoek.aangevraagd_door} vraagt u om een scan of foto van uw <b>{verzoek.type_label?.toLowerCase()}</b> te
                uploaden voor uw personeelsdossier. Maak een duidelijke foto van de voor- én achterkant.
                Uw document wordt vertrouwelijk opgeslagen en is alleen zichtbaar voor HR/management.
              </div>
            </AlertDescription>
          </Alert>

          <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
                 onChange={(e) => { voegToe(e.target.files); e.target.value = ""; }} data-testid="id-camera-input" />
          <input ref={uploadRef} type="file" accept="image/*,.pdf" multiple className="hidden"
                 onChange={(e) => { voegToe(e.target.files); e.target.value = ""; }} data-testid="id-upload-input" />

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => cameraRef.current?.click()} className="h-16 flex-col gap-1" data-testid="id-camera-btn">
              <Camera className="h-5 w-5" />
              <span className="text-xs">Foto maken / scannen</span>
            </Button>
            <Button variant="outline" onClick={() => uploadRef.current?.click()} className="h-16 flex-col gap-1" data-testid="id-bestand-btn">
              <Upload className="h-5 w-5" />
              <span className="text-xs">Bestand kiezen (foto/PDF)</span>
            </Button>
          </div>

          {bestanden.length > 0 && (
            <div className="space-y-1" data-testid="id-bestanden-lijst">
              {bestanden.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-slate-50 border rounded px-2 py-1.5">
                  <span className="truncate">{f.name} <span className="text-slate-400">({Math.round(f.size / 1024)} kB)</span></span>
                  <button onClick={() => setBestanden(bestanden.filter((_, x) => x !== i))} className="text-rose-500 ml-2"
                          data-testid={`id-bestand-weg-${i}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <Button onClick={upload} disabled={busy || !bestanden.length}
                  className="w-full bg-[hsl(var(--brand-red))] hover:bg-[hsl(var(--brand-red))]/90 text-white"
                  data-testid="id-upload-versturen">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploaden…</> : `Verstuur ${bestanden.length || ""} bestand(en)`}
          </Button>
          <Button variant="ghost" onClick={later} className="w-full text-slate-500" data-testid="id-later-btn">
            Later — vraag mij bij de volgende login opnieuw
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
